import { supabase } from './supabase';

class CreditService {
  constructor() {
    this.supabase = supabase;
  }

  // Get user credit information
  async getUserCredits() {
    try {
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session) {
        throw new Error('User not authenticated');
      }

      const userId = session.user.id;
      const currentDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

      // Get all active plans and select the highest priority one
      const { data: activePlans, error: activePlansError } = await this.supabase
        .from('email_validation_credits')
        .select('id, credits_total, credits_remaining, credits_used, is_unlimited, reset_date, subscription_plan, billing_cycle, activation_date')
        .eq('user_id', userId)
        .lte('activation_date', currentDate + 'T23:59:59Z') // activation_date <= current date
        .gte('reset_date', currentDate) // reset_date >= current date
        .order('activation_date', { ascending: false }); // Get all active plans

      if (activePlansError) {
        console.error('Error getting active plans:', activePlansError);
        throw new Error('Failed to fetch credit information');
      }

      if (!activePlans || activePlans.length === 0) {
        // Return default free plan data if no active plans
        return {
          credits: {
            total: 100,
            used: 0,
            remaining: 100,
            usage_percentage: 0
          },
          subscription: {
            plan: 'free',
            billing_cycle: 'monthly',
            is_active: true,
            is_unlimited: false,
            expires_at: null
          }
        };
      }

      // Select the highest priority plan
      const activePlan = this.selectHighestPriorityPlan(activePlans);

      if (!activePlan) {
        // Return default free plan data if no valid plan found
        return {
          credits: {
            total: 100,
            used: 0,
            remaining: 100,
            usage_percentage: 0
          },
          subscription: {
            plan: 'free',
            billing_cycle: 'monthly',
            is_active: true,
            is_unlimited: false,
            expires_at: null
          }
        };
      }

      // Get total credits
      const totalCredits = activePlan.credits_total;
      const usagePercentage = totalCredits > 0 ? Math.round((activePlan.credits_used / totalCredits) * 100) : 0;

      return {
        credits: {
          total: totalCredits,
          used: activePlan.credits_used,
          remaining: activePlan.credits_remaining,
          usage_percentage: usagePercentage
        },
        subscription: {
          plan: activePlan.subscription_plan,
          billing_cycle: activePlan.billing_cycle,
          is_active: true,
          is_unlimited: activePlan.is_unlimited,
          expires_at: activePlan.reset_date
        }
      };
    } catch (error) {
      // console.error('Error getting user credits:', error);
      throw error;
    }
  }

  // Check if user has sufficient credits for validation
  async hasSufficientCredits(requiredCredits = 1) {
    try {
      const creditInfo = await this.getUserCredits();
      
      // Unlimited plans always have sufficient credits
      if (creditInfo.subscription.is_unlimited) {
        return true;
      }
      
      // Check if subscription is active
      if (!creditInfo.subscription.is_active) {
        return false;
      }
      
      // Check if user has enough credits
      return creditInfo.credits.remaining >= requiredCredits;
    } catch (error) {
      console.error('Error checking sufficient credits:', error);
      return false;
    }
  }

  // Consume credits for validation using date-based filtering with priority logic
  async consumeCredits(creditsToUse = 1) {
    try {
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session) {
        throw new Error('User not authenticated');
      }

      const currentDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      
      // Get all active plans and select the highest priority one
      const { data: activePlans, error: activePlansError } = await this.supabase
        .from('email_validation_credits')
        .select('id, credits_remaining, credits_used, is_unlimited, reset_date, subscription_plan, billing_cycle, activation_date')
        .eq('user_id', session.user.id)
        .lte('activation_date', currentDate + 'T23:59:59Z') // activation_date <= current date
        .gte('reset_date', currentDate) // reset_date >= current date
        .order('activation_date', { ascending: false }); // Get all active plans

      if (activePlansError || !activePlans || activePlans.length === 0) {
        console.error('Error getting active plans for credit consumption:', activePlansError?.message);
        return false;
      }

      // Select the highest priority plan
      const activePlan = this.selectHighestPriorityPlan(activePlans);

      if (!activePlan) {
        // console.error('No active plan found for credit consumption');
        return false;
      }

      // Check if subscription is still active (not expired) - now using reset_date
      if (activePlan.reset_date && new Date(activePlan.reset_date) < new Date()) {
        console.error('Subscription has expired (reset_date:', activePlan.reset_date, ')');
        return false;
      }

      // For unlimited plans, just update credits_used
      if (activePlan.is_unlimited) {
        const { error: unlimitedUpdateError } = await this.supabase
          .from('email_validation_credits')
          .update({
            credits_used: activePlan.credits_used + creditsToUse,
            last_updated: new Date().toISOString()
          })
          .eq('id', activePlan.id);

        if (unlimitedUpdateError) {
          console.error('Error updating unlimited credits:', unlimitedUpdateError.message);
          return false;
        }
        return true;
      } else {
        // For limited plans, check sufficient credits and update
        if (activePlan.credits_remaining < creditsToUse) {
          console.error(`Insufficient credits: ${activePlan.credits_remaining} remaining, ${creditsToUse} required`);
          return false;
        }

        const { error: limitedUpdateError } = await this.supabase
          .from('email_validation_credits')
          .update({
            credits_used: activePlan.credits_used + creditsToUse,
            credits_remaining: Math.max(0, activePlan.credits_remaining - creditsToUse),
            last_updated: new Date().toISOString()
          })
          .eq('id', activePlan.id);

        if (limitedUpdateError) {
          console.error('Error updating limited credits:', limitedUpdateError.message);
          return false;
        }
        return true;
      }
    } catch (error) {
      console.error('Error consuming credits:', error);
      return false;
    }
  }

  // Get credit usage statistics
  async getCreditStats() {
    try {
      const creditInfo = await this.getUserCredits();
      
      return {
        total: creditInfo.credits.total,
        used: creditInfo.credits.used,
        remaining: creditInfo.credits.remaining,
        usagePercentage: creditInfo.credits.usage_percentage,
        isUnlimited: creditInfo.subscription.is_unlimited,
        isActive: creditInfo.subscription.is_active,
        plan: creditInfo.subscription.plan,
        billingCycle: creditInfo.subscription.billing_cycle,
        expiresAt: creditInfo.subscription.expires_at
      };
    } catch (error) {
      // console.error('Error getting credit stats:', error);
      return null;
    }
  }

  // Format credit display
  formatCredits(credits) {
    if (credits === null || credits === undefined) {
      return '0';
    }
    
    // Check if credits represent unlimited (999999999)
    if (credits === 999999999) {
      return 'Unlimited';
    }
    
    if (credits >= 1000000) {
      return `${(credits / 1000000).toFixed(1)}M`;
    } else if (credits >= 1000) {
      return `${(credits / 1000).toFixed(1)}K`;
    } else {
      return credits.toString();
    }
  }

  // Format credits for display with locale string formatting
  formatCreditsDisplay(credits) {
    if (credits === null || credits === undefined) {
      return '0';
    }
    
    // Check if credits represent unlimited (999999999)
    if (credits === 999999999) {
      return 'Unlimited';
    }
    
    return credits.toLocaleString();
  }

  // Get plan display name
  getPlanDisplayName(plan, billingCycle, isUnlimited) {
    if (isUnlimited) {
      // Enterprise plans show as "Enterprise (Monthly/Yearly)" with unlimited credits
      const billingNames = {
        'monthly': 'Monthly',
        'yearly': 'Yearly'
      };
      const billingName = billingNames[billingCycle] || billingCycle;
      return `Enterprise (${billingName}) - Unlimited`;
    }
    
    const planNames = {
      'Pro': 'Pro',
      'Enterprise': 'Enterprise',
      'free': 'Free'
    };
    
    const billingNames = {
      'monthly': 'Monthly',
      'yearly': 'Yearly'
    };
    
    const planName = planNames[plan] || plan;
    const billingName = billingNames[billingCycle] || billingCycle;
    
    return `${planName} (${billingName})`;
  }

  // Get credit limit for plan
  getCreditLimit(plan, billingCycle) {
    const baseCredits = {
      'Pro': 10000,
      'Enterprise': 10000,
      'free': 100
    };
    
    const base = baseCredits[plan] || 100;
    
    if (billingCycle === 'yearly') {
      return base * 12;
    } else if (billingCycle === 'monthly') {
      return base;
    } else {
      return base; // Default to monthly
    }
  }

  // Check if user can upgrade
  async canUpgrade() {
    try {
      const creditInfo = await this.getUserCredits();
      return creditInfo.subscription.plan === 'free';
    } catch (error) {
      console.error('Error checking upgrade eligibility:', error);
      return false;
    }
  }

  // Get upgrade recommendations
  async getUpgradeRecommendations() {
    try {
      const creditInfo = await this.getUserCredits();
      const recommendations = [];
      
      if (creditInfo.credits.usage_percentage > 80) {
        recommendations.push({
          type: 'high_usage',
          message: 'You\'re using most of your credits. Consider upgrading to get more validations.',
          priority: 'high'
        });
      }
      
      if (!creditInfo.subscription.is_active) {
        recommendations.push({
          type: 'expired_subscription',
          message: 'Your subscription has expired. Renew to continue using premium features.',
          priority: 'high'
        });
      }
      
      if (creditInfo.subscription.plan === 'free' && creditInfo.credits.usage_percentage > 50) {
        recommendations.push({
          type: 'free_plan_usage',
          message: 'You\'re using half of your free credits. Upgrade to Pro for more validations.',
          priority: 'medium'
        });
      }
      
      return recommendations;
    } catch (error) {
      console.error('Error getting upgrade recommendations:', error);
      return [];
    }
  }

  // Select the highest priority plan from active plans
  selectHighestPriorityPlan(activePlans) {
    if (!activePlans || activePlans.length === 0) {
      return null;
    }

    // Simple logic: Paid plans always have priority over free plans
    // Among same plan type, prefer most recent activation
    
    const paidPlans = activePlans.filter(plan => plan.subscription_plan !== 'free');
    const freePlans = activePlans.filter(plan => plan.subscription_plan === 'free');
    
    // If there are paid plans, select from them (most recent activation)
    if (paidPlans.length > 0) {
      const sortedPaidPlans = paidPlans.sort((a, b) => 
        new Date(b.activation_date) - new Date(a.activation_date)
      );
      
      const selectedPlan = sortedPaidPlans[0];
      // console.log('Selected paid plan:', {
      //   plan: selectedPlan.subscription_plan,
      //   billing: selectedPlan.billing_cycle,
      //   activation_date: selectedPlan.activation_date,
      //   credits_remaining: selectedPlan.credits_remaining
      // });
      
      return selectedPlan;
    }
    
    // If no paid plans, select from free plans (most recent activation)
    if (freePlans.length > 0) {
      const sortedFreePlans = freePlans.sort((a, b) => 
        new Date(b.activation_date) - new Date(a.activation_date)
      );
      
      const selectedPlan = sortedFreePlans[0];
      // console.log('Selected free plan:', {
      //   plan: selectedPlan.subscription_plan,
      //   billing: selectedPlan.billing_cycle,
      //   activation_date: selectedPlan.activation_date,
      //   credits_remaining: selectedPlan.credits_remaining
      // });
      
      return selectedPlan;
    }
    
    return null;
  }
}

export default new CreditService(); 